const MEMBER_MANAGEMENT_PERMISSIONS = ['members:write'] as const;
const EVENT_MANAGEMENT_PERMISSIONS = ['events:create', 'events:write'] as const;
const NEWS_MANAGEMENT_PERMISSIONS = ['news:publish', 'news:write'] as const;
const DOCUMENT_MANAGEMENT_PERMISSIONS = ['documents:write'] as const;
// Backend erlaubt externe Links auch mit members:write (externalLinks.ts, fallbackPermissions)
const EXTERNAL_LINK_MANAGEMENT_PERMISSIONS = ['externalLinks:write', 'members:write'] as const;

/** Berechtigungen, die den Verwaltungsbereich sichtbar machen (auch Grundlage fuer `isAdmin` im Backend). */
export const ADMIN_AREA_PERMISSIONS = [
  ...MEMBER_MANAGEMENT_PERMISSIONS,
  ...EVENT_MANAGEMENT_PERMISSIONS,
  ...NEWS_MANAGEMENT_PERMISSIONS,
  ...DOCUMENT_MANAGEMENT_PERMISSIONS,
] as const;

type PermissionSubject = { permissions: readonly string[] };

export function hasAnyPermission(user: PermissionSubject, permissions: readonly string[]): boolean {
  return permissions.some((permission) => user.permissions.includes(permission));
}

export function canManageMembers(user: PermissionSubject): boolean {
  return hasAnyPermission(user, MEMBER_MANAGEMENT_PERMISSIONS);
}

export function canManageEvents(user: PermissionSubject): boolean {
  return hasAnyPermission(user, EVENT_MANAGEMENT_PERMISSIONS);
}

export function canManageNews(user: PermissionSubject): boolean {
  return hasAnyPermission(user, NEWS_MANAGEMENT_PERMISSIONS);
}

export function canManageDocuments(user: PermissionSubject): boolean {
  return hasAnyPermission(user, DOCUMENT_MANAGEMENT_PERMISSIONS);
}

export function canManageExternalLinks(user: PermissionSubject): boolean {
  return hasAnyPermission(user, EXTERNAL_LINK_MANAGEMENT_PERMISSIONS);
}

/** Zugang zum Verwaltungsbereich. Bevorzugter Name in den Apps. */
export function hasAdminAreaAccess(permissions?: readonly string[] | null): boolean {
  if (!permissions) return false;
  return hasAnyPermission({ permissions }, ADMIN_AREA_PERMISSIONS);
}

/** Alias fuer Aufrufer mit User-Objekt (Backend). */
export function canAccessAdminArea(user: PermissionSubject): boolean {
  return hasAdminAreaAccess(user.permissions);
}

export interface AdminSectionAccess {
  events: boolean;
  news: boolean;
  members: boolean;
  documents: boolean;
  /** Benachrichtigungen bei Mitgliederdaten-Aenderungen (members:write). */
  notifications: boolean;
  /** Vereinseinstellungen inkl. Logo (members:write). */
  orgSettings: boolean;
  /** Bexio und weitere Integrationen (members:write). */
  integrations: boolean;
  externalLinks: boolean;
  /** Mindestens ein Bereich sichtbar; identisch mit hasAdminAreaAccess. */
  any: boolean;
}

/** Sichtbarkeit der einzelnen Verwaltungsbereiche, gleich fuer Web und Mobile. */
export function getAdminSectionAccess(permissions?: readonly string[] | null): AdminSectionAccess {
  const user = { permissions: permissions ?? [] };
  const members = canManageMembers(user);
  return {
    events: canManageEvents(user),
    news: canManageNews(user),
    members,
    documents: canManageDocuments(user),
    notifications: members,
    orgSettings: members,
    integrations: members,
    externalLinks: canManageExternalLinks(user),
    any: hasAdminAreaAccess(user.permissions),
  };
}
